import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { useEffect, useRef } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { Base } from "./components/base";
import { FETCH_ERROR } from "./consts/fetch-error";
import { Home } from "./pages/home";
import { Login } from "./pages/login";
import { Logout } from "./pages/logout";
import { Report } from "./pages/report";
import { Time } from "./pages/time";
import { TimeCreate } from "./pages/time-create";
import { TimeEdit } from "./pages/time-edit";
import { ConfirmProvider } from "./providers/confirm";
import { logout } from "./services/auth";
import { on } from "./services/event-listner";
import { useAuthStore } from "./stores/auth";

export function App() {
  const queryClient = new QueryClient();

  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ConfirmProvider>
          <BrowserRouter>
            <Paths />
          </BrowserRouter>
        </ConfirmProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

function Paths() {
  const navigate = useNavigate();
  const location = useLocation();
  const authStore = useAuthStore();

  const setIsAuthRef = useRef(authStore.setIsAuth);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/home");
    }
  });

  useEffect(() => {
    let handleEsc: (event: KeyboardEvent) => void;
    if (!authStore.isAuth) {
      // Listen to esc key
      handleEsc = (event: KeyboardEvent) => {
        if (event.ctrlKey && event.key === "Escape") {
          // navigate to login page
          navigate("/login");
        }
      };
      window.addEventListener("keydown", handleEsc);
    }
    return () => {
      if (handleEsc) {
        window.removeEventListener("keydown", handleEsc);
      }
    };
  });

  // Logout and redirect to home if fetch error is encountered
  useEffect(() => {
    const { unsubscribe } = on("error", (error) => {
      if (error === FETCH_ERROR.UNAUTHORIZED) {
        (async () => {
          await logout();
          setIsAuthRef.current(false);
          navigateRef.current("/");
        })();
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <Routes>
      <Route
        index
        element={
          <Base>
            <Home />
          </Base>
        }
        path="/home"
      />
      <Route
        element={
          <Base>
            <Time />
          </Base>
        }
        path="/time"
      >
        <Route element={<TimeCreate />} path="create" />
        <Route element={<TimeEdit />} path="edit/:id" />
      </Route>
      <Route
        element={
          <Base>
            <Report />
          </Base>
        }
        path="/report"
      />
      <Route
        element={
          <Base>
            <Login />
          </Base>
        }
        path="/login"
      />
      <Route element={<Logout />} path="/logout" />
    </Routes>
  );
}
